import { useMemo } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
  lexAiWebSocketBaseUrl,
  getToken,
} from 'data_sources/constants';

/** Temp: Flutter x-api-key for Lex AI WebSocket; other app features use REACT_APP_API_GATEWAY_KEY. */
const LEX_AI_WEBSOCKET_API_KEY = 'lUoj9gi00wG0fiv5QDFCOo3O';

/**
 * Builds the full WebSocket URL with query params (x-api-key, authorization).
 */
export function buildLexAiWebSocketUrl(): string {
  const base = lexAiWebSocketBaseUrl();
  if (!base) return '';
  const token = getToken();
  const params = new URLSearchParams();
  params.set('x-api-key', LEX_AI_WEBSOCKET_API_KEY);
  if (token) params.set('authorization', token);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export type UseLexAiWebSocketResult = {
  sendJson: (payload: object) => void;
  lastJsonMessage: object | null;
  readyState: ReadyState;
  isOpen: boolean;
};

/**
 * WebSocket connection for Lex AI chat.
 * Connects to the URL from lexAiWebSocketBaseUrl() with x-api-key and authorization query params.
 * Pass getWebSocketUrl so it is called when the socket is created (fresh token). When it returns null, no connection is made.
 */
export function useLexAiWebSocket(getWebSocketUrl: () => string | null): UseLexAiWebSocketResult {
  const url = getWebSocketUrl();
  const shouldConnect = !!url;

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    url ?? '',
    { shouldReconnect: () => false, reconnectAttempts: 0 },
    shouldConnect
  );

  const sendJson = useMemo(
    () => (payload: object) => {
      sendJsonMessage(payload as object);
    },
    [sendJsonMessage]
  );

  const isOpen = readyState === ReadyState.OPEN;

  return {
    sendJson,
    lastJsonMessage: lastJsonMessage as object | null,
    readyState,
    isOpen,
  };
}
